/**
 * Lo spostamento del fuoco è **spaziale**, non segue l'ordine del documento.
 *
 * La formula è quella del prototipo v3 (`move()`), portata pari pari: fra tutti i
 * candidati nella direzione giusta vince quello con distanza minore **più due volte e
 * mezzo lo scarto perpendicolare**. Vale la pena implementarla con quella formula e non
 * con una euristica propria — è il modo in cui si evita che la freccia destra da una
 * locandina finisca in una voce del menu laterale che sta a mezzo schermo di distanza
 * (03-SCHERMATE-TV §1).
 *
 * Qui dentro non ci sono elementi del DOM: ci sono rettangoli. È la terza giunzione
 * (01-ARCHITETTURA §2.4): chi legge i rettangoli dallo schermo è un adattatore, la
 * regola con cui si sceglie è del dominio e non cambia su Tizen.
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface FocusCandidate<T> {
  readonly ref: T;
  readonly rect: Rect;
}

/** Peso dello scarto perpendicolare. Prototipo v3: `sc = max(0, p) + s2 * 2.5`. */
export const PERPENDICULAR_WEIGHT = 2.5;

/**
 * Tolleranza sulla sovrapposizione: un candidato che comincia appena prima del bordo
 * dell'elemento corrente è ancora «nella direzione giusta». Prototipo v3: 8 px.
 */
export const OVERLAP_TOLERANCE_PX = 8;

function perpendicularDistance(from: number, to: number, center: number): number {
  if (center >= from && center <= to) return 0;
  return Math.min(Math.abs(from - center), Math.abs(to - center));
}

export function navigationScore(current: Rect, candidate: Rect, direction: Direction): number | null {
  const centerX = (current.left + current.right) / 2;
  const centerY = (current.top + current.bottom) / 2;
  let alongAxis: number;
  let perpendicular: number;

  switch (direction) {
    case 'right':
      if (candidate.left < current.right - OVERLAP_TOLERANCE_PX) return null;
      alongAxis = candidate.left - current.right;
      perpendicular = perpendicularDistance(candidate.top, candidate.bottom, centerY);
      break;
    case 'left':
      if (candidate.right > current.left + OVERLAP_TOLERANCE_PX) return null;
      alongAxis = current.left - candidate.right;
      perpendicular = perpendicularDistance(candidate.top, candidate.bottom, centerY);
      break;
    case 'down':
      if (candidate.top < current.bottom - OVERLAP_TOLERANCE_PX) return null;
      alongAxis = candidate.top - current.bottom;
      perpendicular = perpendicularDistance(candidate.left, candidate.right, centerX);
      break;
    case 'up':
      if (candidate.bottom > current.top + OVERLAP_TOLERANCE_PX) return null;
      alongAxis = current.top - candidate.bottom;
      perpendicular = perpendicularDistance(candidate.left, candidate.right, centerX);
      break;
  }

  return Math.max(0, alongAxis) + perpendicular * PERPENDICULAR_WEIGHT;
}

/**
 * Il candidato migliore, o `null` se in quella direzione non c'è niente.
 *
 * `null` non è un guasto: dal bordo della griglia la freccia non fa niente, ed è il
 * comportamento giusto.
 */
export function findNextFocus<T>(
  current: Rect,
  candidates: ReadonlyArray<FocusCandidate<T>>,
  direction: Direction,
): T | null {
  let best: T | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const score = navigationScore(current, candidate.rect, direction);
    if (score === null) continue;
    if (score < bestScore) {
      bestScore = score;
      best = candidate.ref;
    }
  }
  return best;
}
